import React, { useState } from 'react';
type Segment = 'investor' | 'retailer' | 'qsr';
type Lang = 'en' | 'de';
type SegmentConfig = {
  label: string;
  title: string;
  description: string;
  companyLabel: string;
  companyPlaceholder: string;
  dropdownLabel: string;
  submitLabel: string;
  successHeadline: string;
  successBody: string;
  options: {
    value: string;
    label: string;
  }[];
};
type UIStrings = {
  brandSubtitle: string;
  contactName: string;
  namePlaceholder: string;
  emailLabel: string;
  emailPlaceholder: string;
  submitting: string;
  submissionError: string;
  requestLogged: string;
  confirmationPrefix: string;
  copySent: string;
  submitAnother: string;
  networkError: string;
  submissionFailed: string;
  footerLine2: string;
};
const FORMSPREE_ENDPOINT = 'https://formspree.io/f/xpqnvlew';
const UI: Record<Lang, UIStrings> = {
  en: {
    brandSubtitle: 'Commercial Portal',
    contactName: 'Contact Name',
    namePlaceholder: 'Name',
    emailLabel: 'Professional Email',
    emailPlaceholder: 'name@organization.com',
    submitting: 'Submitting…',
    submissionError: 'Submission Error',
    requestLogged: 'Request Logged',
    confirmationPrefix: 'Confirmation #',
    copySent: 'A copy has been sent to your inbox.',
    submitAnother: 'Submit Another Request',
    networkError: 'Network error. Please try again or email partners@neurospice.com directly.',
    submissionFailed: 'Submission failed. Please try again or email partners@neurospice.com directly.',
    footerLine2: 'Berlin Hub • Confidential Profile'
  },
  de: {
    brandSubtitle: 'Geschäftsportal',
    contactName: 'Kontaktname',
    namePlaceholder: 'Name',
    emailLabel: 'Geschäftliche E-Mail',
    emailPlaceholder: 'name@unternehmen.com',
    submitting: 'Wird gesendet…',
    submissionError: 'Übertragungsfehler',
    requestLogged: 'Anfrage erfasst',
    confirmationPrefix: 'Bestätigung Nr. ',
    copySent: 'Eine Kopie wurde an Ihr Postfach gesendet.',
    submitAnother: 'Weitere Anfrage senden',
    networkError: 'Netzwerkfehler. Bitte erneut versuchen oder direkt an partners@neurospice.com schreiben.',
    submissionFailed: 'Übertragung fehlgeschlagen. Bitte erneut versuchen oder direkt an partners@neurospice.com schreiben.',
    footerLine2: 'Berlin Hub • Vertraulich'
  }
};
const SEGMENTS: Record<Lang, Record<Segment, SegmentConfig>> = {
  en: {
    investor: {
      label: 'Investor',
      title: 'Access the Pitch Deck',
      description: "Tell us about your firm and we'll send the pre-seed investment framework over directly.",
      companyLabel: 'Firm / Fund Name',
      companyPlaceholder: 'Entity Name',
      dropdownLabel: 'Investment Profile',
      submitLabel: 'Send the Deck',
      successHeadline: 'Vault Access Pending Review',
      successBody: 'Your credentials are under review by the Neurospice founding team. Expect a tracked deck link within 2 business days.',
      options: [{
        value: 'angel',
        label: 'Venture Angel / Private Investor'
      }, {
        value: 'vc',
        label: 'Venture Capital Fund'
      }, {
        value: 'family_office',
        label: 'Family Office'
      }, {
        value: 'syndicate',
        label: 'Syndicate / Syndicate Lead'
      }]
    },
    retailer: {
      label: 'Retailer',
      title: 'Request Retail Distribution Kit',
      description: 'Access wholesale tier packaging documentation, certified stability testing data sheets, logistics profiles, and EU grocery margin structures.',
      companyLabel: 'Retailer / Group Name',
      companyPlaceholder: 'Entity Name',
      dropdownLabel: 'Distribution Scale',
      submitLabel: 'Send the Trade Kit',
      successHeadline: 'Distribution Kit Dispatched',
      successBody: 'A download link for the wholesale documentation pack is on its way to your inbox. Our retail lead will follow up within 1 business day.',
      options: [{
        value: 'boutique',
        label: 'Independent Wellness Boutique'
      }, {
        value: 'regional',
        label: 'Regional Organic Grocery Chain'
      }, {
        value: 'national',
        label: 'National Grocery / Supermarket Network'
      }, {
        value: 'ecom',
        label: 'Specialized E-Commerce Platform'
      }]
    },
    qsr: {
      label: 'Foodservice',
      title: 'Inquire for Foodservice / Bulk Supply',
      description: 'Connect regarding commercial formulation integrations, recipe profile heat stability data, bulk loose ingredient sourcing, and pilot menu test options.',
      companyLabel: 'Venue / Brand Group',
      companyPlaceholder: 'Entity Name',
      dropdownLabel: 'Operation Format',
      submitLabel: "Send the Chef's Brief",
      successHeadline: 'Briefing Pack On Its Way',
      successBody: 'The foodservice briefing and bulk supply sheet will arrive in your inbox shortly. Our commercial team will reach out to schedule a pilot conversation.',
      options: [{
        value: 'restaurant',
        label: 'Restaurant Group / Multi-Site Operator'
      }, {
        value: 'bar',
        label: 'Bar / Cocktail Program'
      }, {
        value: 'cafe_group',
        label: 'Specialty Coffee / Café Group'
      }, {
        value: 'hospitality',
        label: 'Hotel / Hospitality Group'
      }, {
        value: 'qsr_chain',
        label: 'Quick Service Restaurant Chain'
      }, {
        value: 'catering',
        label: 'Catering / Corporate Foodservice'
      }]
    }
  },
  de: {
    investor: {
      label: 'Investor',
      title: 'Zugang zum Pitch Deck',
      description: 'Erzählen Sie uns kurz von Ihrer Firma — wir schicken Ihnen den Pre-Seed-Investitionsrahmen direkt zu.',
      companyLabel: 'Firma / Fondsname',
      companyPlaceholder: 'Firmenname',
      dropdownLabel: 'Investmentprofil',
      submitLabel: 'Schickt mir das Deck',
      successHeadline: 'Zugang in Prüfung',
      successBody: 'Ihre Zugangsdaten werden vom Neurospice-Gründerteam geprüft. Sie erhalten innerhalb von 2 Werktagen einen getrackten Deck-Link.',
      options: [{
        value: 'angel',
        label: 'Venture Angel / Privatinvestor'
      }, {
        value: 'vc',
        label: 'Venture-Capital-Fonds'
      }, {
        value: 'family_office',
        label: 'Family Office'
      }, {
        value: 'syndicate',
        label: 'Syndikat / Syndikatsleitung'
      }]
    },
    retailer: {
      label: 'Handel',
      title: 'Vertriebsunterlagen anfordern',
      description: 'Greifen Sie auf Großhandels-Verpackungsdokumentation, zertifizierte Stabilitätsdaten, Logistikprofile und EU-Handelsmargenstrukturen zu.',
      companyLabel: 'Händler / Gruppenname',
      companyPlaceholder: 'Firmenname',
      dropdownLabel: 'Vertriebsumfang',
      submitLabel: 'Schickt mir das Handelspaket',
      successHeadline: 'Vertriebspaket versendet',
      successBody: 'Ein Download-Link zur Großhandelsdokumentation ist bereits unterwegs in Ihr Postfach. Unser Retail-Lead meldet sich innerhalb von 1 Werktag.',
      options: [{
        value: 'boutique',
        label: 'Unabhängige Wellness-Boutique'
      }, {
        value: 'regional',
        label: 'Regionale Bio-Handelskette'
      }, {
        value: 'national',
        label: 'Nationales Lebensmittel- / Supermarkt-Netzwerk'
      }, {
        value: 'ecom',
        label: 'Spezialisierte E-Commerce-Plattform'
      }]
    },
    qsr: {
      label: 'Gastronomie',
      title: 'Anfrage Gastronomie / Großgebinde',
      description: 'Kontaktaufnahme zu kommerziellen Rezepturintegrationen, Hitzestabilitätsdaten für Rezeptprofile, Großgebinde-Bezug und Pilotmenü-Tests.',
      companyLabel: 'Betrieb / Markengruppe',
      companyPlaceholder: 'Firmenname',
      dropdownLabel: 'Betriebsform',
      submitLabel: 'Schickt mir das Küchen-Briefing',
      successHeadline: 'Briefing-Paket unterwegs',
      successBody: 'Das Gastronomie-Briefing und das Großgebinde-Datenblatt erreichen Sie in Kürze per E-Mail. Unser Vertriebsteam meldet sich für ein Pilotgespräch.',
      options: [{
        value: 'restaurant',
        label: 'Restaurantgruppe / Multi-Standort-Betrieb'
      }, {
        value: 'bar',
        label: 'Bar / Cocktail-Programm'
      }, {
        value: 'cafe_group',
        label: 'Specialty Coffee / Café-Gruppe'
      }, {
        value: 'hospitality',
        label: 'Hotel / Hospitality-Gruppe'
      }, {
        value: 'qsr_chain',
        label: 'Quick-Service-Restaurant-Kette'
      }, {
        value: 'catering',
        label: 'Catering / Betriebsgastronomie'
      }]
    }
  }
};
const SEGMENT_ORDER: Segment[] = ['investor', 'retailer', 'qsr'];
type SubmitState = {
  status: 'idle';
} | {
  status: 'submitting';
} | {
  status: 'success';
} | {
  status: 'error';
  message: string;
};
export function PartnerHub() {
  const [lang, setLang] = useState<Lang>('en');
  const [segment, setSegment] = useState<Segment>('investor');
  const [submitState, setSubmitState] = useState<SubmitState>({
    status: 'idle'
  });
  const config = SEGMENTS[lang][segment];
  const ui = UI[lang];
  const handleSegmentChange = (key: Segment) => {
    setSegment(key);
    if (submitState.status === 'success') setSubmitState({
      status: 'idle'
    });
  };
  const handleLangChange = (next: Lang) => {
    setLang(next);
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    if (formData.get('_gotcha')) {
      setSubmitState({
        status: 'success'
      });
      return;
    }
    formData.set('segment', segment);
    formData.set('segment_label', SEGMENTS.en[segment].label);
    formData.set('language', lang);
    formData.set('_subject', `[Neurospice Hub] New ${SEGMENTS.en[segment].label} request (${lang.toUpperCase()})`);
    setSubmitState({
      status: 'submitting'
    });
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        body: formData,
        headers: {
          Accept: 'application/json'
        }
      });
      if (response.ok) {
        form.reset();
        setSubmitState({
          status: 'success'
        });
      } else {
        const data = await response.json().catch(() => null);
        const message = data?.errors?.[0]?.message || ui.submissionFailed;
        setSubmitState({
          status: 'error',
          message
        });
      }
    } catch {
      setSubmitState({
        status: 'error',
        message: ui.networkError
      });
    }
  };
  const isSubmitting = submitState.status === 'submitting';
  const isSuccess = submitState.status === 'success';
  return <main className="flex w-full min-h-screen justify-center items-center bg-white text-[#111111] px-4 py-10 overflow-x-hidden" style={{
    fontFamily: "'Inter', sans-serif"
  }} data-id="element-1">
      <section className="w-full max-w-[500px] p-6 sm:p-10 border border-[#111111] bg-white" style={{
      boxShadow: '0px 4px 25px rgba(0, 0, 0, 0.04)'
    }} data-id="element-2">
        {/* Language utility bar */}
        <div className="flex justify-end items-center gap-1 text-[8.5pt] uppercase font-bold tracking-[1px] mb-3" role="group" aria-label="Language" data-id="element-3">
          <button type="button" onClick={() => handleLangChange('en')} aria-pressed={lang === 'en'} className={`px-1.5 py-0.5 transition-colors duration-150 cursor-pointer ${lang === 'en' ? 'text-[#111111] underline underline-offset-4' : 'text-[#999999] hover:text-[#111111]'}`} data-id="element-4">
            EN
          </button>
          <span className="text-[#cccccc]" data-id="element-5">/</span>
          <button type="button" onClick={() => handleLangChange('de')} aria-pressed={lang === 'de'} className={`px-1.5 py-0.5 transition-colors duration-150 cursor-pointer ${lang === 'de' ? 'text-[#111111] underline underline-offset-4' : 'text-[#999999] hover:text-[#111111]'}`} data-id="element-6">
            DE
          </button>
        </div>

        {/* Brand header */}
        <header className="border-b-2 border-[#111111] pb-4 mb-6" data-id="element-7">
          <h1 className="m-0 text-[22pt] sm:text-[26pt] font-extrabold leading-[0.95] tracking-[-1.5px] break-words" data-id="element-8">
            NEUROSPICE®
          </h1>
          <p className="mt-1.5 text-[10pt] italic text-[#111111] font-medium tracking-[-0.2px]" data-id="element-9">
            Season with Reason
          </p>
          <p className="mt-1.5 text-[8.5pt] uppercase tracking-[2px] text-[#555555] font-semibold" data-id="element-10">
            {ui.brandSubtitle}
          </p>
        </header>

        {/* Product banner strip */}
        <div className="mb-6 border-y border-[#111111] overflow-hidden" data-id="element-11">
          <img src="https://cdn.magicpatterns.com/uploads/mGxusongAxLaNcmQ3qiZMM/Gemini_Generated_Image_o570mgo570mgo570.png" alt="Neurospice functional spice packaging line" className="w-full h-24 object-cover block" data-id="element-12" />
        </div>

        {/* Segment selector */}
        <div className="flex gap-2 mb-6" role="tablist" aria-label="Partner segment" data-id="element-13">
          {SEGMENT_ORDER.map(key => {
          const isActive = segment === key;
          return <button key={key} type="button" role="tab" aria-selected={isActive} onClick={() => handleSegmentChange(key)} className={`flex-1 px-1 py-2.5 text-[8pt] uppercase font-bold tracking-[0.5px] text-center cursor-pointer border transition-colors duration-200 ${isActive ? 'bg-[#111111] text-white border-[#111111]' : 'bg-[#fafafa] text-[#111111] border-[#e0e0e0] hover:bg-[#f0f0f0]'}`} data-id="element-14">
                {SEGMENTS[lang][key].label}
              </button>;
        })}
        </div>

        {/* Dynamic title + description */}
        <h2 className="text-[13pt] uppercase tracking-[0.5px] m-0 mb-2 font-extrabold" data-id="element-15">
          {isSuccess ? config.successHeadline : config.title}
        </h2>
        <p className="text-[9.5pt] text-[#444444] leading-[1.5] m-0 mb-6" data-id="element-16">
          {isSuccess ? config.successBody : config.description}
        </p>

        {isSuccess ? <div data-id="element-17">
            <div className="border border-[#111111] bg-[#fafafa] px-4 py-4 text-[9pt] leading-[1.5] text-[#111111]" data-id="element-18">
              <div className="text-[7.5pt] uppercase font-bold tracking-[1px] text-[#555555] mb-1" data-id="element-19">
                {ui.requestLogged}
              </div>
              {ui.confirmationPrefix}
              {Math.random().toString(36).slice(2, 8).toUpperCase()} —{' '}
              {ui.copySent}
            </div>
            <button type="button" onClick={() => setSubmitState({
          status: 'idle'
        })} className="w-full mt-4 px-4 py-3.5 bg-white text-[#111111] text-[9.5pt] uppercase tracking-[1px] font-bold cursor-pointer border border-[#111111] hover:bg-[#111111] hover:text-white transition-colors duration-200" data-id="element-20">
              {ui.submitAnother}
            </button>
          </div> : <form onSubmit={handleSubmit} noValidate data-id="element-21">
            <div aria-hidden="true" style={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          padding: 0,
          margin: '-1px',
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          whiteSpace: 'nowrap',
          border: 0
        }} data-id="element-22">
              <input type="text" name="_gotcha" tabIndex={-1} autoComplete="off" data-id="element-23" />
            </div>

            <div className="mb-4" data-id="element-24">
              <label htmlFor="name" className="block text-[8pt] uppercase font-bold tracking-[0.5px] mb-1.5 text-[#333333]" data-id="element-25">
                {ui.contactName}
              </label>
              <input id="name" name="name" type="text" required disabled={isSubmitting} placeholder={ui.namePlaceholder} className="w-full px-3 py-3 text-[9.5pt] border border-[#cccccc] bg-white focus:outline-none focus:border-[#111111] disabled:bg-[#fafafa] disabled:text-[#888888]" data-id="element-26" />
            </div>

            <div className="mb-4" data-id="element-27">
              <label htmlFor="email" className="block text-[8pt] uppercase font-bold tracking-[0.5px] mb-1.5 text-[#333333]" data-id="element-28">
                {ui.emailLabel}
              </label>
              <input id="email" name="email" type="email" required disabled={isSubmitting} placeholder={ui.emailPlaceholder} className="w-full px-3 py-3 text-[9.5pt] border border-[#cccccc] bg-white focus:outline-none focus:border-[#111111] disabled:bg-[#fafafa] disabled:text-[#888888]" data-id="element-29" />
            </div>

            <div className="mb-4" data-id="element-30">
              <label htmlFor="company" className="block text-[8pt] uppercase font-bold tracking-[0.5px] mb-1.5 text-[#333333]" data-id="element-31">
                {config.companyLabel}
              </label>
              <input id="company" name="company" type="text" required disabled={isSubmitting} placeholder={config.companyPlaceholder} className="w-full px-3 py-3 text-[9.5pt] border border-[#cccccc] bg-white focus:outline-none focus:border-[#111111] disabled:bg-[#fafafa] disabled:text-[#888888]" data-id="element-32" />
            </div>

            <div className="mb-4" data-id="element-33">
              <label htmlFor="type-select" className="block text-[8pt] uppercase font-bold tracking-[0.5px] mb-1.5 text-[#333333]" data-id="element-34">
                {config.dropdownLabel}
              </label>
              <select id="type-select" name="profile_type" disabled={isSubmitting} className="w-full px-3 py-3 text-[9.5pt] border border-[#cccccc] bg-white focus:outline-none focus:border-[#111111] disabled:bg-[#fafafa] disabled:text-[#888888]" data-id="element-35">
                {config.options.map(opt => <option key={opt.value} value={opt.value} data-id="element-36">
                    {opt.label}
                  </option>)}
              </select>
            </div>

            {submitState.status === 'error' && <div role="alert" className="mb-4 border border-[#111111] bg-white px-3 py-2.5 text-[8.5pt] text-[#111111] leading-[1.4]" data-id="element-37">
                <span className="font-bold uppercase tracking-[0.5px] text-[7.5pt]" data-id="element-38">
                  {ui.submissionError} —{' '}
                </span>
                {submitState.message}
              </div>}

            <button type="submit" disabled={isSubmitting} className="w-full mt-2 px-4 py-3.5 bg-[#111111] text-white text-[9.5pt] uppercase tracking-[1px] font-bold cursor-pointer hover:bg-[#333333] transition-colors duration-200 disabled:bg-[#555555] disabled:cursor-not-allowed" data-id="element-39">
              {isSubmitting ? ui.submitting : config.submitLabel}
            </button>
          </form>}

        {/* Footer */}
        <footer className="mt-8 pt-4 flex justify-between items-center" style={{
        borderTop: '1px dashed #e5e5e5'
      }} data-id="element-40">
          <div className="text-[7.5pt] text-[#666666] uppercase tracking-[0.5px] leading-[1.4]" data-id="element-41">
            Neurospice® B2B Networks
            <br data-id="element-42" />
            {ui.footerLine2}
          </div>
          <div className="text-right" data-id="element-43">
            <img src="https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=https://project-generous-shrimp-958.magicpatterns.app/" alt="Prototype link QR code" className="w-11 h-11 border border-[#111111] bg-white" data-id="element-44" />
          </div>
        </footer>
      </section>
    </main>;
}